<template>
  <div class="x-container-max d-flex fd-column flex-1-1 bd-t-gray30 p-0">
    <div class="MESSAGES-WRAPPER">
      <message-empty
          v-if="!messages.length"
          :job="job"
          class="WELCOME-MESSAGE"/>

      <transition name="fade">
        <div
            v-infinite-scroll.reverse="getMoreMessages"
            v-if="messages.length"
            ref="messages"
            class="MESSAGES flex-1-1 fd-column max-container p-x-1">

          <transition name="fade">
            <div
                v-if="hasMore"
                key="loading"
                :style="`height: ${hasMore ? '100px' : '0px'}`"
                class="loading-wrapper d-flex ai-center jc-center flex-0-0">
              <loading-indicator v-if="loadingMore" :size="48" />
            </div>
          </transition>

          <transition-group
              v-for="(messages, date, index) in groupedMessages"
              :key="date"
              name="list"
              tag="div"
              class="message-group">
            <h3 :key="index" class="bd-b-gray50 p-b-1 m-0" selenium="message-date-header-label">
              {{ date }}
            </h3>
            <transition-group :key="date" :name="messageTransition" tag="div">
              <message-item
                  v-for="message in messages"
                  :key="message.message_id"
                  :message="message"/>
            </transition-group>
          </transition-group>
        </div>
      </transition>

      <transition name="fade">
        <div
            v-if="isSending"
            class="sending-overlay d-flex fd-column ai-center jc-center">
          <loading-indicator :size="48" />
          <div class="sending-text p-t-1 text-center text-gray70">Sending message...</div>
        </div>
      </transition>
    </div>
    <div v-if="messageDisabled" class="d-flex fd-column ai-center  p-b-2" >
      <div class="text-center text-bold p-b-2">
        Oops! We’re sorry, this Pro is no longer available to talk about your service request.
      </div>
      <router-link
          button
          :to="{name: 'consumer::dashboard'}"
          type="submit"
          class="btn btn-lg"
          selenium="return-to-my-requests-button"> Return to My Requests </router-link>
    </div>
    <div v-else>
      <message-input
          :job="job"
          :skills-profile-id="skillsProfileId"
          @message-sending="messageSending"
          @message-send-finished="messageSendFinished"
          @message-sent="messageSent"/>
    </div>
  </div>

</template>

<script>
import _sortBy from 'lodash/sortBy';
import PRUtils from '@homeservices/tool-kit/proreferral/utils';

import MessageInput from './MessageInput';
import MessageItem from './MessageItem';
import MessageEmpty from './MessageEmpty';

export default {
  name: 'message-view',
  components: {
    MessageInput,
    MessageItem,
    MessageEmpty,
  },
  props: {
    job: {
      type: Object,
      required: true,
    },
    skillsProfileId: {
      type: Number,
      required: true,
    },
    messages: {
      type: Array,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    hasMore: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    loadingMore: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      messageTransition: 'list',
      isSending: false,
    };
  },
  computed: {
    sortedMessages () { return _sortBy(this.messages, 'time_created'); },
    groupedMessages () {
      return this.sortedMessages
        .filter(message => message.message_type !== 'system')
        .reduce((acc, next) => {
          const formatedDate = this.$options.filters.timeReceivedDefault(
            new Date(next.time_created)
          );
          const existingList = acc[formatedDate] || [];

          return {
            ...acc,
            [formatedDate]: [...existingList, next],
          };
        }, {});
    },
    match () {
      return this.job.matches.find(matchElement =>
        matchElement.service_provider_id === this.skillsProfileId);
    },
    messageDisabled () {
      // No matching pro for this conversation: treat as unavailable instead of crashing
      return !this.match || PRUtils.messages.shouldBeDisabled(this.match.match_status);
    },
  },
  async mounted () {
    try {
      await this.$store.dispatch('consumer/messages/fetchMessages', {
        jobId: this.job.order_hash,
        skillsProfileId: Number(this.skillsProfileId),
      });
      this.scrollToEnd();
      this.markConversationRead();
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  },
  methods: {
    markConversationRead () {
      this.$store.dispatch('consumer/inbox/markConversationRead', {
        messages: this.$store.getters['consumer/messages/getMessages'](
          this.job.order_hash,
          Number(this.skillsProfileId)
        ),
        jobId: this.job.user_friendly_identifier,
        proUserId: this.match && this.match.service_provider_user_identifier,
        skillsProfileId: Number(this.skillsProfileId),
      });
    },
    async getMoreMessages () {
      if (!this.hasMore) { await Promise.reject(); }

      await this.$store.dispatch('consumer/messages/loadMore', {
        jobId: this.job.id,
        skillsProfileId: Number(this.skillsProfileId),
        params: { size: 10 },
      });
    },
    scrollToEnd () {
      const messages$ = this.$refs.messages;
      if (messages$) {
        messages$.scrollTop = messages$.scrollHeight - messages$.offsetHeight;
      }
    },
    messageSending () {
      // New messages transition from bottom
      // and loaded (old) messages transition from top
      this.messageTransition = 'list-reverse';
      this.isSending = true;
    },
    messageSent () {
      this.messageTransition = 'list';
      this.isSending = false;
      this.scrollToEnd();
    },
    messageSendFinished () {
      this.isSending = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools.scss";

  .MESSAGES-WRAPPER {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    position: relative;
    overflow: auto;
  }

  .WELCOME-MESSAGE {
    @include absolute(20%, 10%, auto, 10%);

    @include tablet {
      top: 35%;
    }

    &.FADED {
      opacity: 0.4;
    }
  }

  .loading-wrapper {
    transition: height 0.4s;
  }

  .message-group {
    @include padding-bottom(2, 4);

    &:last-of-type {
      @include padding-bottom(1, 2); // to be consistent with rest of the container
    }
  }

  .MESSAGES {
    // Firefox doesn't care about padding when absolute for some reason
    // We remove the padding bottom and add it in 'message-group box
    @include padding(1 1 0 1, 2 2 0 2);
    @include absolute(auto, 0, 0, 0);
    max-height: 100%;
    overflow: auto;
    background-color: white;
  }

  .sending-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(255, 255, 255, 0.85);
    z-index: 1000;
  }

  .sending-text {
    font-weight: 600;
  }
</style>
